import type { ReactNode, TextareaHTMLAttributes, InputHTMLAttributes, SelectHTMLAttributes } from 'react'
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
} from 'lucide-react'
import { cx, SEVERITY_STYLE, STATUS_STYLE } from '../lib/format'
import type { CaseStatus, Severity } from '../lib/types'

export function Panel({
  title,
  actions,
  children,
  className,
  bodyClassName,
  icon,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
  icon?: ReactNode
}) {
  return (
    <section className={cx('panel', className)}>
      {title ? (
        <header className="panel-head">
          <h2 className="panel-title flex items-center gap-2">
            {icon ? <span className="text-slate-500">{icon}</span> : null}
            {title}
          </h2>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cx('px-5 py-4', bodyClassName)}>{children}</div>
    </section>
  )
}

export function Field({
  label,
  hint,
  children,
  className,
  required,
}: {
  label: ReactNode
  hint?: ReactNode
  children: ReactNode
  className?: string
  required?: boolean
}) {
  return (
    <label className={cx('block', className)}>
      <span className="label">
        {label}
        {required ? <span className="ml-1 text-altair-400">*</span> : null}
      </span>
      {children}
      {hint ? <span className="mt-1.5 block text-[11.5px] leading-relaxed text-slate-500">{hint}</span> : null}
    </label>
  )
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx('input', className)} {...props} />
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx('input min-h-[96px] resize-y leading-relaxed', className)} {...props} />
}

export function Select({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return (
    <select className={cx('input', className)} {...props}>
      {children}
    </select>
  )
}

export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition',
        checked
          ? 'border-altair-500/40 bg-altair-500/[0.08]'
          : 'border-white/[0.08] bg-white/[0.02] hover:border-white/20',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cx(
          'flex shrink-0 items-center justify-center rounded border transition',
          checked ? 'border-altair-500 bg-altair-500 text-white' : 'border-white/25 bg-transparent',
        )}
        style={{ height: '1.125rem', width: '1.125rem' }}
      >
        {checked ? <Check size={12} strokeWidth={3.5} /> : null}
      </span>
      <span className="min-w-0">
        <span className={cx('block text-[13.5px] font-medium', checked ? 'text-white' : 'text-slate-300')}>
          {label}
        </span>
        {hint ? <span className="mt-0.5 block text-[11.5px] leading-snug text-slate-500">{hint}</span> : null}
      </span>
    </button>
  )
}

export function StatusPill({ status, className }: { status: CaseStatus; className?: string }) {
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.draft
  return (
    <span className={cx('pill', style.className, className)}>
      <span className={cx('h-1.5 w-1.5 rounded-full', style.dot)} />
      {style.label}
    </span>
  )
}

export function SeverityPill({ severity, className }: { severity: Severity; className?: string }) {
  const style = SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.moderate
  return (
    <span className={cx('pill', style.className, className)}>
      {severity === 'severe' ? <AlertTriangle size={11} strokeWidth={2.6} /> : null}
      {style.label}
    </span>
  )
}

export function DataRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="data-row">
      <span className="data-key">{label}</span>
      <span className="data-value">{children}</span>
    </div>
  )
}

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return <Loader2 size={size} className={cx('animate-spin', className)} />
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: ReactNode
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {icon ? (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-500">
          {icon}
        </div>
      ) : null}
      <h3 className="text-[15px] font-semibold text-slate-200">{title}</h3>
      {children ? (
        <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-slate-500">{children}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}

export function Callout({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'success'
  title?: ReactNode
  children: ReactNode
  className?: string
}) {
  const tones = {
    info: 'border-sky-400/20 bg-sky-400/[0.07] text-sky-100',
    warn: 'border-amber-400/25 bg-amber-400/[0.07] text-amber-100',
    danger: 'border-red-500/25 bg-red-500/[0.08] text-red-100',
    success: 'border-emerald-400/25 bg-emerald-400/[0.07] text-emerald-100',
  }
  return (
    <div className={cx('rounded-lg border px-4 py-3 text-[13px] leading-relaxed', tones[tone], className)}>
      {title ? <div className="mb-1 font-semibold">{title}</div> : null}
      <div className="opacity-90">{children}</div>
    </div>
  )
}

/**
 * Builds a compact page list: first and last page always shown, a window around
 * the current page, and `null` where pages were skipped.
 */
function pageWindow(page: number, totalPages: number, span = 1): (number | null)[] {
  const wanted = new Set<number>([1, totalPages])
  for (let offset = -span; offset <= span; offset += 1) {
    const candidate = page + offset
    if (candidate >= 1 && candidate <= totalPages) wanted.add(candidate)
  }

  const sorted = [...wanted].sort((a, b) => a - b)
  const out: (number | null)[] = []
  let previous = 0
  for (const value of sorted) {
    if (previous && value - previous > 1) out.push(null)
    out.push(value)
    previous = value
  }
  return out
}

export function Pagination({
  page,
  perPage,
  total,
  totalPages,
  onPage,
  onPerPage,
  perPageOptions = [12, 24, 48, 96],
}: {
  page: number
  perPage: number
  total: number
  totalPages: number
  onPage: (page: number) => void
  onPerPage?: (perPage: number) => void
  perPageOptions?: number[]
}) {
  if (!total) return null

  const first = (page - 1) * perPage + 1
  const last = Math.min(total, page * perPage)

  const step = (target: number, label: string, icon: ReactNode) => (
    <button
      type="button"
      onClick={() => onPage(target)}
      disabled={target < 1 || target > totalPages || target === page}
      aria-label={label}
      className="btn-subtle px-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-35"
    >
      {icon}
    </button>
  )

  return (
    <div className="no-print mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-5">
      <div className="flex items-center gap-3 text-[12.5px] text-slate-500">
        <span>
          <span className="font-medium text-slate-300">
            {first.toLocaleString()}–{last.toLocaleString()}
          </span>{' '}
          of {total.toLocaleString()}
        </span>
        {onPerPage ? (
          <label className="flex items-center gap-1.5">
            <span className="sr-only">Cases per page</span>
            <select
              value={perPage}
              onChange={(event) => onPerPage(Number(event.target.value))}
              className="input input-sm w-auto py-1"
            >
              {perPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {totalPages > 1 ? (
        <div className="flex items-center gap-1">
          {step(1, 'First page', <ChevronsLeft size={15} />)}
          {step(page - 1, 'Previous page', <ChevronLeft size={15} />)}

          {pageWindow(page, totalPages).map((value, index) =>
            value === null ? (
              <span key={`gap-${index}`} className="px-1 text-[12.5px] text-slate-600">
                …
              </span>
            ) : (
              <button
                key={value}
                type="button"
                onClick={() => onPage(value)}
                aria-current={value === page ? 'page' : undefined}
                className={cx(
                  'min-w-[32px] rounded-md px-2 py-1.5 text-[12.5px] font-medium transition',
                  value === page
                    ? 'bg-white/[0.09] text-white'
                    : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200',
                )}
              >
                {value}
              </button>
            ),
          )}

          {step(page + 1, 'Next page', <ChevronRight size={15} />)}
          {step(totalPages, 'Last page', <ChevronsRight size={15} />)}
        </div>
      ) : null}
    </div>
  )
}

export function SectionHeading({
  step,
  title,
  blurb,
}: {
  step?: number
  title: string
  blurb?: string
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2.5">
        {step ? (
          <span className="flex h-6 w-6 items-center justify-center rounded-md border border-altair-500/35 bg-altair-500/10 text-[11.5px] font-bold text-altair-300">
            {step}
          </span>
        ) : null}
        <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-white">{title}</h2>
      </div>
      {blurb ? <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{blurb}</p> : null}
    </div>
  )
}
